import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import {
  ApplicantDto,
  ApplicantStatusOption,
  ApplicantSubStatusOption,
  ApplicantTransitionOption,
  ListApplicantsParams,
  PaginatedApplicants,
  TransitionApplicantPayload,
  UpdateApplicantPayload,
} from './applicants.service';
import type { WorkflowDocumentation } from './leads.service';

function toQuery(params: ListApplicantsParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('pageSize', String(params.pageSize));
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.statusId) q.set('statusId', params.statusId);
  if (params.preferredDestination?.trim()) q.set('preferredDestination', params.preferredDestination.trim());
  if (params.createdFrom) q.set('createdFrom', params.createdFrom);
  if (params.createdTo) q.set('createdTo', params.createdTo);
  if (params.archiveFilter) q.set('archiveFilter', params.archiveFilter);
  if (params.ownerUserId) q.set('ownerUserId', params.ownerUserId);
  if (params.showDisqualified) q.set('showDisqualified', 'true');
  const s = q.toString();
  return s ? `?${s}` : '';
}

@Injectable({ providedIn: 'root' })
export class TenantApplicantsService {
  private readonly http = inject(HttpClient);

  constructor(private readonly api: ApiService) {}

  list(params: ListApplicantsParams = {}) {
    return this.api
      .get<ApiEnvelope<PaginatedApplicants>>(`tenant/applicants${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  listStatuses() {
    return this.api
      .get<ApiEnvelope<ApplicantStatusOption[]>>('tenant/applicants/statuses')
      .pipe(map((r) => r.data));
  }

  getById(id: string) {
    return this.api.get<ApiEnvelope<ApplicantDto>>(`tenant/applicants/${id}`).pipe(map((r) => r.data));
  }

  update(id: string, payload: UpdateApplicantPayload) {
    return this.api
      .patch<ApiEnvelope<ApplicantDto>>(`tenant/applicants/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  uploadProfilePhoto(id: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http
      .post<ApiEnvelope<ApplicantDto>>(`${this.api.baseUrl}/tenant/applicants/${id}/profile-photo`, form, {
        withCredentials: true,
      })
      .pipe(map((r) => r.data));
  }

  archive(id: string) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`tenant/applicants/${id}/archive`, {})
      .pipe(map((r) => r.data));
  }

  unarchive(id: string) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`tenant/applicants/${id}/unarchive`, {})
      .pipe(map((r) => r.data));
  }

  getTransitions(id: string) {
    return this.api
      .get<ApiEnvelope<ApplicantTransitionOption[]>>(`tenant/applicants/${id}/transitions`)
      .pipe(map((r) => r.data));
  }

  getWorkflowDocumentation() {
    return this.api
      .get<ApiEnvelope<WorkflowDocumentation>>('tenant/applicants/workflow')
      .pipe(map((r) => r.data));
  }

  transition(id: string, payload: TransitionApplicantPayload) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`tenant/applicants/${id}/transition`, payload)
      .pipe(map((r) => r.data));
  }

  listSubStatuses() {
    return this.api
      .get<ApiEnvelope<ApplicantSubStatusOption[]>>(`tenant/applicants/sub-statuses`)
      .pipe(map((r) => r.data));
  }

  updateSubStatus(id: string, subStatusId: string | null) {
    return this.api
      .patch<ApiEnvelope<ApplicantDto>>(`tenant/applicants/${id}/sub-status`, { subStatusId })
      .pipe(map((r) => r.data));
  }

  assignOwner(id: string, ownerUserId: string) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`tenant/applicants/${id}/assign`, { ownerUserId })
      .pipe(map((r) => r.data));
  }

  remove(id: string) {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`tenant/applicants/${id}`)
      .pipe(map((r) => r.data));
  }
}
